import { type ReactNode, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { type ProductData, ProductsElement, type TProductsElement } from '@shared/dream-components';

import Text from '~/components/Text';
import useIsMobile from '~/hooks/useIsMobile';
import usePaginatedProducts from '~/hooks/usePaginatedProducts';
import useProductReviewSummaries from '~/hooks/useProductReviewSummaries';

interface Props {
  node: TProductsElement;
  children: ReactNode | ReactNode[];
}

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex max-w-2xl flex-col space-y-2 py-8">
    <Text as="p" type="body" size="md" className="text-center leading-tight tracking-normal">
      {message}
    </Text>
  </div>
);

const getArchiveOffsetProducts = (products: ProductData[], productsOffset: number) => {
  return products.slice(productsOffset);
};

const calculatePaginationOffset = (productsOffset: number, perPage: number): number => {
  if (!productsOffset || !perPage || productsOffset <= 0 || perPage <= 0) return 0;
  const result = Math.floor(productsOffset / perPage);
  return Number.isNaN(result) ? 0 : result;
};

const calculateAdjustedTotalPages = (totalPages: number, pagesSkipped: number): number => {
  if (!totalPages || totalPages <= 0) return 0;
  if (!pagesSkipped || pagesSkipped <= 0) return totalPages;
  const result = Math.max(totalPages - pagesSkipped, 0);
  return Number.isNaN(result) ? 0 : result;
};

const ProductsBase = ({ node, children }: Props) => {
  const isViewportMobile = useIsMobile();
  const { t } = useTranslation();

  const {
    hasFakeData,
    data: { products: defaultProducts = [], pagination },
    productGroupType,
    productsOffset = 0,
    productsCount,
    productsTagIds,
  } = node.attrs;

  const parsedTagIds = useMemo(() => {
    if (!productsTagIds) return undefined;
    try {
      const parsed = JSON.parse(productsTagIds);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch {
      // not valid JSON
    }
    return undefined;
  }, [productsTagIds]);

  const isArchive = productGroupType === 'archive';
  const isFreeSelection = productGroupType === 'free_selection' || !productGroupType;

  const perPage = pagination?.per_page || 3;
  const pagesSkippedByOffset = isArchive ? calculatePaginationOffset(productsOffset, perPage) : 0;
  const adjustedTotalPages = calculateAdjustedTotalPages(pagination?.total_pages || 0, pagesSkippedByOffset);

  const {
    products: paginatedProducts,
    loadMoreProducts,
    goToPage,
    currentPage,
  } = usePaginatedProducts({
    defaultProducts: isArchive ? getArchiveOffsetProducts(defaultProducts, productsOffset) : defaultProducts,
    pagination: pagination || null,
    enabled: hasFakeData === false && !isFreeSelection,
    productGroupType: productGroupType || 'free_selection',
    hasFakeData: hasFakeData || false,
    pagesSkippedByOffset,
    blockId: node.attrs.id,
    displayPerPage: productsCount,
    fetchPerPage: isArchive ? (productsCount || 3) + productsOffset : undefined,
    productTagIds: parsedTagIds,
  });

  const shouldUseFetchedProducts = !!pagination && !isFreeSelection;
  const renderableProducts = shouldUseFetchedProducts ? paginatedProducts : defaultProducts;

  const productIds = useMemo(() => {
    return renderableProducts.map((p) => p.id).filter(Boolean);
  }, [renderableProducts]);

  const { summaries } = useProductReviewSummaries({
    productIds,
    enabled: productIds.length > 0,
  });

  const productsWithSummaries = useMemo(() => {
    return renderableProducts.map((product) => ({
      ...product,
      reviews_summary: summaries[product.id] || product.reviews_summary || null,
    }));
  }, [renderableProducts, summaries]);

  return (
    <ProductsElement
      isLive
      element={{
        ...node,
        attrs: {
          ...node.attrs,
          data: {
            ...node.attrs.data,
            products: productsWithSummaries,
          },
        },
      }}
      viewport={isViewportMobile ? 'mobile' : 'desktop'}
      translations={{
        'product.free': t('products.free_label'),
        'product_reviews.review': t('product_reviews.review'),
        'product_reviews.reviews': t('product_reviews.reviews'),
      }}
      paginateCurrentPage={currentPage}
      paginateTotalPages={adjustedTotalPages}
      paginateOnPageChange={goToPage}
      paginateOnLoadMore={loadMoreProducts}
    >
      {children}
    </ProductsElement>
  );
};

const Products = ({ node, children }: Props) => {
  return (
    <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
      <ProductsBase node={node}>{children}</ProductsBase>
    </ErrorBoundary>
  );
};

export default Products;
